import React, { createContext, useContext, useState, Dispatch, SetStateAction, useCallback, useMemo, useEffect } from 'react';
import { CatalogFilter, CategoryType } from '../types';
import lodash from 'lodash';
import { useSearchParams, useLocation, useMatch } from 'react-router';
import { useAppState } from './useAppState';

const { isEqual } = lodash;

// Helper function to normalize object by sorting keys
const normalizeObject = (obj: Record<string, any>): Record<string, any> => {
    if (!obj || typeof obj !== 'object') return obj;

    const sortedKeys = Object.keys(obj).sort();
    const normalized: Record<string, any> = {};

    for (const key of sortedKeys) {
        const value = obj[key];
        normalized[key] = Array.isArray(value)
            ? value.map(normalizeObject)
            : typeof value === 'object' && value !== null
                ? normalizeObject(value)
                : value;
    }

    return normalized;
};

interface CatalogFilterContextType {
    filters: CatalogFilter;
    setFilters: Dispatch<SetStateAction<CatalogFilter>>;
    updateFilter: <K extends keyof CatalogFilter>(key: K, value: CatalogFilter[K]) => void;
    clearFilter: (key: keyof CatalogFilter) => void;
    resetFilters: () => void;
}

const defaultFilters: CatalogFilter = {
    price: undefined,
    duration: undefined,
    categories: [],
    sorting: undefined,
    amount: undefined,
};

const CatalogFilterContext = createContext<CatalogFilterContextType | undefined>(undefined);

export const CatalogFilterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const matchCategory = useMatch('/catalog/:slug');
    const [categories] = useAppState<CategoryType[]>("categories", []);
    const location = useLocation();
    const isCatalogPage = useMemo(() => {
        return location.pathname === '/catalog' || matchCategory;
    }, [location.pathname, matchCategory]);

  

    const [filters, setFilters] = useState<CatalogFilter>((() => {
        {
            const params = new URLSearchParams(location.search);
            const queryFilter = params.get("filter");
            let initialFilters = defaultFilters;
    
            if (queryFilter) {
                try {
                    initialFilters = JSON.parse(queryFilter);
                } catch (error) {
                    console.error("Failed to parse initial filter", error);
                }
            }
            // If we have a category slug in the URL, find its ID and add to filters
            if (matchCategory?.params.slug) {
                const category = categories.find((cat) => cat.slug === matchCategory.params.slug);
                if (category?.id) {
                    initialFilters = {
                        ...initialFilters,
                        categories: [category.id]
                    };
                }
            }
            return initialFilters;
        }
    })());



    const serializedFilters = useMemo(() => JSON.stringify(filters), [filters]);
    const parsedFilters = useMemo<CatalogFilter>(() => {
        const queryFilter = searchParams.get("filter");
        try {
            return queryFilter ? JSON.parse(queryFilter) : defaultFilters;
        } catch (error) {
            console.error("Failed to parse filter query parameter", error);
            return defaultFilters;
        }
    }, [searchParams]);

    // Keep URL in sync with filter changes
    useEffect(() => {
        // Skip filter synchronization if we're not on a catalog page
        if (!isCatalogPage) return;

        const normalizedParsedFilters = normalizeObject(parsedFilters);
        const normalizedFilters = normalizeObject(Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== undefined))); //filter undefined values

        if (!isEqual(normalizedParsedFilters, normalizedFilters)) {
            setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);
                newParams.set("filter", serializedFilters);
                return newParams;
            }, { replace: true });
        }
    }, [filters, serializedFilters, setSearchParams, parsedFilters, isCatalogPage]);

    const clearFilter = useCallback((key: keyof CatalogFilter) => {
        setFilters(prev => {
            const updatedFilters = { ...prev };
            delete updatedFilters[key];
            return updatedFilters;
        });
    }, []);

    const resetFilters = useCallback(() => {
        // If we're in a category page, keep the category filter
        if (matchCategory?.params.slug) {
            const category = categories.find((cat) => cat.slug === matchCategory.params.slug);
            if (category?.id) {
                setFilters({
                    ...defaultFilters,
                    categories: [category.id]
                });
                return;
            }
        }
        setFilters(defaultFilters);
    }, [matchCategory?.params.slug, categories]);

    const updateFilter = useCallback(<K extends keyof CatalogFilter>(key: K, value: CatalogFilter[K]) => {
        setFilters(prev => ({ ...prev, [key]: value}));
    }, []);

    const value = useMemo(() => ({
        filters,
        setFilters,
        updateFilter,
        clearFilter,
        resetFilters,
    }), [filters, updateFilter, clearFilter, resetFilters]);

    return (
        <CatalogFilterContext.Provider value={value}>
            {children}
        </CatalogFilterContext.Provider>
    );
};

export const useCatalogFilter = () => {
    const context = useContext(CatalogFilterContext);
    if (context === undefined) {
        throw new Error('useCatalogFilter must be used within a CatalogFilterProvider');
    }
    return context;
}; 
